import { useState, useCallback, useEffect, useMemo, useId } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, AppSnackbar } from '../../components/ui';
import { useKnowledgeBase } from '../../contexts/KnowledgeBaseContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArticleCategory,
  ArticleType,
  Department,
  ARTICLE_CATEGORIES,
  DEPARTMENTS,
  ARTICLE_TYPE_LABELS,
  createDefaultFormContent,
  type ArticleFormData,
  type ArticleFormErrors,
  type StepFormData,
  type HowToFormData,
  type KnownErrorFormData,
  type SOPRunbookFormData,
} from '../../types/knowledgeBase';
import { StepEditor } from './components/StepEditor';
import { KnownErrorEditor } from './components/KnownErrorEditor';
import { SOPRunbookEditor } from './components/SOPRunbookEditor';
import './ArticleEditor.css';

const EMPTY_STEP: StepFormData = {
  title: '',
  content: '',
};

/**
 * Get steps from article content (works for both how-to and sop-runbook)
 */
function getStepsFromContent(content: ArticleFormData['articleContent']): StepFormData[] {
  if (content.type === 'how-to') {
    return content.content.steps;
  }
  if (content.type === 'sop-runbook') {
    return content.content.procedureSteps;
  }
  return [];
}

/**
 * Create initial form data based on article type
 */
function createInitialFormData(articleType: ArticleType): ArticleFormData {
  return {
    title: '',
    description: '',
    category: ArticleCategory.OTHER,
    tags: [],
    articleType,
    articleContent: createDefaultFormContent(articleType),
    audiences: [Department.ALL],
    isPublished: true,
  };
}

/**
 * Create form data from existing article
 */
function createFormDataFromArticle(article: {
  title: string;
  description: string;
  category: ArticleCategory;
  tags: string[];
  articleType: ArticleType;
  articleContent: { type: string; content: unknown };
  audiences: Department[];
  isPublished: boolean;
  steps: Array<{
    id: string;
    title: string;
    content: string;
    imageUrl?: string;
    imageAlt?: string;
  }>;
}): ArticleFormData {
  // Handle both new format and legacy format
  if (article.articleContent) {
    return {
      title: article.title,
      description: article.description,
      category: article.category,
      tags: article.tags,
      articleType: article.articleType,
      articleContent: article.articleContent as ArticleFormData['articleContent'],
      audiences: article.audiences,
      isPublished: article.isPublished,
    };
  }

  // Legacy format - convert steps to how-to content
  return {
    title: article.title,
    description: article.description,
    category: article.category,
    tags: article.tags,
    articleType: ArticleType.HOW_TO,
    articleContent: {
      type: 'how-to',
      content: {
        steps: article.steps.map((s) => ({
          id: s.id,
          title: s.title,
          content: s.content,
          imageUrl: s.imageUrl,
          imageAlt: s.imageAlt,
        })),
      },
    },
    audiences: [Department.ALL],
    isPublished: article.isPublished,
  };
}

export function ArticleEditor() {
  const { id, type: typeParam } = useParams<{ id: string; type: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { getArticleById, createArticle, updateArticle } = useKnowledgeBase();

  const isEditing = Boolean(id);
  const existingArticle = id ? getArticleById(id) : undefined;

  // Determine article type from URL param, search param, or existing article
  const articleType: ArticleType = useMemo(() => {
    if (existingArticle) return existingArticle.articleType;
    if (typeParam && Object.values(ArticleType).includes(typeParam as ArticleType)) {
      return typeParam as ArticleType;
    }
    const typeFromSearch = searchParams.get('type');
    if (typeFromSearch && Object.values(ArticleType).includes(typeFromSearch as ArticleType)) {
      return typeFromSearch as ArticleType;
    }
    return ArticleType.HOW_TO;
  }, [existingArticle, typeParam, searchParams]);

  // Form state
  const [formData, setFormData] = useState<ArticleFormData>(() => {
    if (existingArticle) {
      return createFormDataFromArticle(existingArticle);
    }
    return createInitialFormData(articleType);
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<ArticleFormErrors>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Generate unique IDs for accessibility
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-description`;
  const categoryId = `${baseId}-category`;
  const tagsId = `${baseId}-tags`;

  // Update form if editing and article changes
  useEffect(() => {
    if (existingArticle) {
      setFormData(createFormDataFromArticle(existingArticle));
    }
  }, [existingArticle]);

  // Get steps for current content type (how-to or sop-runbook procedure steps)
  const currentSteps = useMemo(() => {
    return getStepsFromContent(formData.articleContent);
  }, [formData.articleContent]);

  // Validation
  const validateForm = useCallback((): boolean => {
    const newErrors: ArticleFormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    if (formData.audiences.length === 0) {
      newErrors.audiences = 'Select at least one audience';
    }

    // Type-specific validation
    switch (formData.articleContent.type) {
      case 'how-to': {
        const steps = formData.articleContent.content.steps;
        if (steps.length === 0) {
          newErrors.steps = 'At least one step is required';
        } else {
          const hasEmptyStep = steps.some((step) => !step.title.trim() || !step.content.trim());
          if (hasEmptyStep) {
            newErrors.steps = 'All steps must have a title and content';
          }
        }
        break;
      }
      case 'known-error': {
        const content = formData.articleContent.content;
        if (content.affectedSystems.length === 0) {
          newErrors.affectedSystems = 'At least one affected system is required';
        }
        if (!content.symptoms.trim() || content.symptoms.trim().length < 20) {
          newErrors.symptoms = 'Symptoms must be at least 20 characters';
        }
        if (!content.workaround.trim() || content.workaround.trim().length < 20) {
          newErrors.workaround = 'Workaround must be at least 20 characters';
        }
        break;
      }
      case 'sop-runbook': {
        const content = formData.articleContent.content;
        if (!content.objective.trim() || content.objective.trim().length < 20) {
          newErrors.objective = 'Objective must be at least 20 characters';
        }
        if (content.prerequisites.length === 0) {
          newErrors.prerequisites = 'At least one prerequisite is required';
        }
        if (content.procedureSteps.length === 0) {
          newErrors.procedureSteps = 'At least one procedure step is required';
        } else {
          const hasEmptyStep = content.procedureSteps.some(
            (step) => !step.title.trim() || !step.content.trim()
          );
          if (hasEmptyStep) {
            newErrors.procedureSteps = 'All procedure steps must have a title and content';
          }
        }
        if (!content.verification.trim() || content.verification.trim().length < 20) {
          newErrors.verification = 'Verification must be at least 20 characters';
        }
        if (content.emergencyContacts.length === 0) {
          newErrors.emergencyContacts = 'At least one emergency contact is required';
        } else {
          const hasInvalidContact = content.emergencyContacts.some(
            (c) => !c.name.trim() || !c.role.trim() || !c.contact.trim()
          );
          if (hasInvalidContact) {
            newErrors.emergencyContacts = 'All contacts must have name, role, and contact info';
          }
        }
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handlers
  const handleFieldChange = useCallback(
    (field: keyof ArticleFormData, value: string | ArticleCategory | boolean | Department[]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof ArticleFormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  const handleTagInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  }, []);

  const handleTagInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const newTag = tagInput.trim().toLowerCase();
        if (newTag && !formData.tags.includes(newTag)) {
          setFormData((prev) => ({
            ...prev,
            tags: [...prev.tags, newTag],
          }));
        }
        setTagInput('');
      }
    },
    [tagInput, formData.tags]
  );

  const handleRemoveTag = useCallback((tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  }, []);

  const handleAudienceToggle = useCallback((dept: Department) => {
    setFormData((prev) => {
      let newAudiences: Department[];

      if (dept === Department.ALL) {
        // If selecting "All", clear others
        newAudiences = prev.audiences.includes(Department.ALL) ? [] : [Department.ALL];
      } else {
        // Remove "All" when selecting specific department
        const withoutAll = prev.audiences.filter((d) => d !== Department.ALL);
        if (withoutAll.includes(dept)) {
          newAudiences = withoutAll.filter((d) => d !== dept);
        } else {
          newAudiences = [...withoutAll, dept];
        }
      }

      return { ...prev, audiences: newAudiences };
    });
    setErrors((prev) => ({ ...prev, audiences: undefined }));
  }, []);

  // Step handlers (for How-To content)
  const handleStepChange = useCallback((index: number, stepData: StepFormData) => {
    setFormData((prev) => {
      if (prev.articleContent.type !== 'how-to') return prev;
      const newSteps = prev.articleContent.content.steps.map((step, i) =>
        i === index ? stepData : step
      );
      return {
        ...prev,
        articleContent: {
          type: 'how-to',
          content: { steps: newSteps },
        },
      };
    });
    setErrors((prev) => ({ ...prev, steps: undefined }));
  }, []);

  const handleAddStep = useCallback(() => {
    setFormData((prev) => {
      if (prev.articleContent.type !== 'how-to') return prev;
      return {
        ...prev,
        articleContent: {
          type: 'how-to',
          content: {
            steps: [...prev.articleContent.content.steps, { ...EMPTY_STEP }],
          },
        },
      };
    });
  }, []);

  const handleRemoveStep = useCallback((index: number) => {
    setFormData((prev) => {
      if (prev.articleContent.type !== 'how-to') return prev;
      return {
        ...prev,
        articleContent: {
          type: 'how-to',
          content: {
            steps: prev.articleContent.content.steps.filter((_, i) => i !== index),
          },
        },
      };
    });
  }, []);

  const handleMoveStep = useCallback((index: number, direction: 'up' | 'down') => {
    setFormData((prev) => {
      if (prev.articleContent.type !== 'how-to') return prev;
      const newSteps = [...prev.articleContent.content.steps];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= newSteps.length) return prev;

      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      return {
        ...prev,
        articleContent: {
          type: 'how-to',
          content: { steps: newSteps },
        },
      };
    });
  }, []);

  // Known Error content handler
  const handleKnownErrorChange = useCallback((content: KnownErrorFormData) => {
    setFormData((prev) => ({
      ...prev,
      articleContent: {
        type: 'known-error',
        content,
      },
    }));
    // Clear any related errors
    setErrors((prev) => ({
      ...prev,
      affectedSystems: undefined,
      symptoms: undefined,
      workaround: undefined,
    }));
  }, []);

  // SOP/Runbook content handler
  const handleSOPRunbookChange = useCallback((content: SOPRunbookFormData) => {
    setFormData((prev) => ({
      ...prev,
      articleContent: {
        type: 'sop-runbook',
        content,
      },
    }));
    // Clear any related errors
    setErrors((prev) => ({
      ...prev,
      objective: undefined,
      prerequisites: undefined,
      procedureSteps: undefined,
      verification: undefined,
      emergencyContacts: undefined,
    }));
  }, []);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSave = useCallback(
    async (publish: boolean) => {
      const dataToSave = { ...formData, isPublished: publish };

      if (!validateForm()) {
        setSnackbar({
          open: true,
          message: 'Please fix the errors before saving',
          severity: 'error',
        });
        return;
      }

      try {
        if (isEditing && id) {
          await updateArticle(id, dataToSave);
          setSnackbar({
            open: true,
            message: 'Article updated successfully',
            severity: 'success',
          });
          // Navigate back after a short delay for edits
          setTimeout(() => {
            navigate(`/knowledge-base/article/${id}`);
          }, 500);
        } else {
          const newArticle = await createArticle(dataToSave);
          setSnackbar({
            open: true,
            message: 'Article created successfully',
            severity: 'success',
          });
          // Navigate to the new article after a short delay
          setTimeout(() => {
            navigate(`/knowledge-base/article/${newArticle.id}`);
          }, 500);
        }
      } catch {
        setSnackbar({
          open: true,
          message: 'Failed to save article',
          severity: 'error',
        });
      }
    },
    [formData, validateForm, isEditing, id, createArticle, updateArticle, navigate]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Check if form has unsaved changes
  const hasChanges = useMemo(() => {
    if (!existingArticle) return true;
    return (
      formData.title !== existingArticle.title ||
      formData.description !== existingArticle.description ||
      formData.category !== existingArticle.category ||
      JSON.stringify(formData.tags) !== JSON.stringify(existingArticle.tags) ||
      JSON.stringify(formData.audiences) !== JSON.stringify(existingArticle.audiences) ||
      JSON.stringify(formData.articleContent) !== JSON.stringify(existingArticle.articleContent)
    );
  }, [formData, existingArticle]);

  const pageTitle = isEditing
    ? `Edit ${ARTICLE_TYPE_LABELS[articleType]}`
    : `Create ${ARTICLE_TYPE_LABELS[articleType]}`;

  // Only render steps editor for How-To articles (for now)
  const showStepsEditor = formData.articleContent.type === 'how-to';

  return (
    <Box className="article-editor" data-theme={theme}>
      <Box className="article-editor__container">
        {/* Header */}
        <Box className="article-editor__header">
          <Typography variant="h5" component="h1" className="article-editor__title">
            {pageTitle}
          </Typography>
          <button className="article-editor__cancel" onClick={handleCancel}>
            Cancel
          </button>
        </Box>

        {/* Form */}
        <Box className="article-editor__form">
          {/* Title */}
          <Box className="article-editor__field">
            <label htmlFor={titleId} className="article-editor__label">
              Title <span className="article-editor__required" aria-hidden="true">*</span>
            </label>
            <input
              id={titleId}
              type="text"
              className={`article-editor__input ${errors.title ? 'article-editor__input--error' : ''}`}
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="e.g., Jabber Password Reset"
              aria-required="true"
              aria-invalid={Boolean(errors.title)}
              aria-describedby={errors.title ? `${titleId}-error` : undefined}
            />
            {errors.title && (
              <span id={`${titleId}-error`} className="article-editor__error" role="alert">
                {errors.title}
              </span>
            )}
          </Box>

          {/* Description */}
          <Box className="article-editor__field">
            <label htmlFor={descriptionId} className="article-editor__label">
              Description <span className="article-editor__required" aria-hidden="true">*</span>
            </label>
            <textarea
              id={descriptionId}
              className={`article-editor__textarea ${errors.description ? 'article-editor__textarea--error' : ''}`}
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Brief summary of what this guide covers..."
              rows={3}
              aria-required="true"
              aria-invalid={Boolean(errors.description)}
              aria-describedby={errors.description ? `${descriptionId}-error` : undefined}
            />
            {errors.description && (
              <span id={`${descriptionId}-error`} className="article-editor__error" role="alert">
                {errors.description}
              </span>
            )}
          </Box>

          {/* Category and Tags Row */}
          <Box className="article-editor__row">
            <Box className="article-editor__field article-editor__field--half">
              <label htmlFor={categoryId} className="article-editor__label">
                Category <span className="article-editor__required" aria-hidden="true">*</span>
              </label>
              <select
                id={categoryId}
                className="article-editor__select"
                value={formData.category}
                onChange={(e) => handleFieldChange('category', e.target.value as ArticleCategory)}
                aria-required="true"
              >
                {ARTICLE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </Box>

            <Box className="article-editor__field article-editor__field--half">
              <label htmlFor={tagsId} className="article-editor__label">Tags</label>
              <Box className="article-editor__tags-input" role="group" aria-label="Tags">
                {formData.tags.map((tag) => (
                  <span key={tag} className="article-editor__tag">
                    {tag}
                    <button
                      className="article-editor__tag-remove"
                      onClick={() => handleRemoveTag(tag)}
                      type="button"
                      aria-label={`Remove tag: ${tag}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <input
                  id={tagsId}
                  type="text"
                  className="article-editor__tag-input"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagInputKeyDown}
                  placeholder={formData.tags.length === 0 ? 'Type and press Enter' : ''}
                />
              </Box>
            </Box>
          </Box>

          {/* Audience Section */}
          <Box className="article-editor__field">
            <span id={`${baseId}-audience`} className="article-editor__label">
              Audience <span className="article-editor__required" aria-hidden="true">*</span>
            </span>
            <Box
              className="article-editor__audience-chips"
              role="group"
              aria-labelledby={`${baseId}-audience`}
            >
              {DEPARTMENTS.map((dept) => (
                <button
                  key={dept}
                  type="button"
                  className={`article-editor__audience-chip ${
                    formData.audiences.includes(dept) ? 'article-editor__audience-chip--selected' : ''
                  }`}
                  onClick={() => handleAudienceToggle(dept)}
                  aria-pressed={formData.audiences.includes(dept)}
                >
                  {dept}
                </button>
              ))}
            </Box>
            {errors.audiences && (
              <span className="article-editor__error" role="alert">
                {errors.audiences}
              </span>
            )}
          </Box>

          {/* Steps Section (for How-To only) */}
          {showStepsEditor && formData.articleContent.type === 'how-to' && (() => {
            const howToContent = formData.articleContent as { type: 'how-to'; content: HowToFormData };
            const steps = howToContent.content.steps;
            return (
              <Box className="article-editor__steps-section">
                <Box className="article-editor__steps-header">
                  <Typography variant="h6" className="article-editor__steps-title">
                    Steps
                  </Typography>
                  {errors.steps && <span className="article-editor__error">{errors.steps}</span>}
                </Box>

                <Box className="article-editor__steps">
                  {steps.map((step, index) => (
                    <StepEditor
                      key={step.id ?? index}
                      step={step}
                      stepNumber={index + 1}
                      totalSteps={steps.length}
                      onChange={(stepData) => handleStepChange(index, stepData)}
                      onRemove={() => handleRemoveStep(index)}
                      onMoveUp={() => handleMoveStep(index, 'up')}
                      onMoveDown={() => handleMoveStep(index, 'down')}
                      canRemove={steps.length > 1}
                    />
                  ))}
                </Box>

                <button className="article-editor__add-step" onClick={handleAddStep} type="button">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M8 3v10M3 8h10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Add Step
                </button>
              </Box>
            );
          })()}

          {/* Known Error Editor */}
          {formData.articleContent.type === 'known-error' && (
            <KnownErrorEditor
              content={formData.articleContent.content}
              onChange={handleKnownErrorChange}
              errors={{
                affectedSystems: errors.affectedSystems,
                symptoms: errors.symptoms,
                workaround: errors.workaround,
              }}
            />
          )}

          {/* SOP/Runbook Editor */}
          {formData.articleContent.type === 'sop-runbook' && (
            <SOPRunbookEditor
              content={formData.articleContent.content}
              onChange={handleSOPRunbookChange}
              errors={{
                objective: errors.objective,
                prerequisites: errors.prerequisites,
                procedureSteps: errors.procedureSteps,
                verification: errors.verification,
                emergencyContacts: errors.emergencyContacts,
              }}
            />
          )}
        </Box>

        {/* Footer Actions */}
        <Box className="article-editor__actions">
          <button
            className="article-editor__save-draft"
            onClick={() => handleSave(false)}
            type="button"
            disabled={!hasChanges}
          >
            Save as Draft
          </button>
          <button
            className="article-editor__publish"
            onClick={() => handleSave(true)}
            type="button"
          >
            {isEditing ? 'Update Article' : 'Publish Article'}
          </button>
        </Box>
      </Box>

      <AppSnackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
